import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-primary-red hover:text-primary-red-dark mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour à l'accueil
        </Link>

        <article className="prose prose-lg max-w-none">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="text-primary-red" size={32} />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-0">
              Politique de confidentialité
            </h1>
          </div>

          <div className="text-sm text-gray-500 mb-8">
            Dernière mise à jour : 26 novembre 2025
          </div>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              ParisPari Hub s'engage à protéger votre vie privée et vos données personnelles. Cette 
              politique de confidentialité explique comment nous collectons, utilisons, stockons et 
              protégeons vos informations personnelles lorsque vous utilisez notre site web parisparihub.com.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              1. Informations que nous collectons
            </h2>

            <p>
              Nous collectons différentes catégories d'informations lorsque vous utilisez notre site :
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Informations que vous nous fournissez directement
            </h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone (si vous nous contactez)</li>
              <li>Messages et communications que vous nous envoyez via le formulaire de contact</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Informations collectées automatiquement
            </h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Adresse IP</li>
              <li>Type de navigateur et version</li>
              <li>Système d'exploitation</li>
              <li>Pages visitées et temps passé sur chaque page</li>
              <li>Date et heure de vos visites</li>
              <li>Référent (site web d'origine)</li>
              <li>Cookies et technologies similaires (voir notre Politique des cookies)</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              2. Comment nous utilisons vos informations
            </h2>

            <p>
              Nous utilisons vos informations personnelles pour les finalités suivantes :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Fournir nos services :</strong> Pour répondre à vos demandes de contact, vous fournir des informations sur les sites de paris, et améliorer votre expérience sur notre site.</li>
              <li><strong>Communication :</strong> Pour répondre à vos questions et demandes via notre formulaire de contact.</li>
              <li><strong>Amélioration du site :</strong> Pour analyser l'utilisation du site, identifier les problèmes techniques, et améliorer nos services.</li>
              <li><strong>Conformité légale :</strong> Pour respecter nos obligations légales et réglementaires.</li>
              <li><strong>Sécurité :</strong> Pour protéger notre site contre la fraude, les abus et les activités illégales.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              3. Base légale du traitement
            </h2>

            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD), nous traitons vos 
              données personnelles sur les bases légales suivantes :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Consentement :</strong> Lorsque vous nous donnez votre consentement explicite (par exemple, pour recevoir des communications marketing).</li>
              <li><strong>Exécution d'un contrat :</strong> Pour répondre à vos demandes et fournir nos services.</li>
              <li><strong>Intérêt légitime :</strong> Pour améliorer notre site, analyser son utilisation, et assurer sa sécurité.</li>
              <li><strong>Obligation légale :</strong> Pour respecter nos obligations légales et réglementaires.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              4. Partage de vos informations
            </h2>

            <p>
              Nous ne vendons jamais vos données personnelles à des tiers. Nous pouvons partager vos 
              informations uniquement dans les cas suivants :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Prestataires de services :</strong> Avec des prestataires qui nous aident à exploiter notre site (hébergement, analyse, etc.), sous contrat strict de confidentialité.</li>
              <li><strong>Obligations légales :</strong> Si la loi l'exige ou si nous recevons une demande légale valide.</li>
              <li><strong>Protection de nos droits :</strong> Pour protéger nos droits, notre propriété, ou la sécurité de nos utilisateurs.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              5. Liens d'affiliation
            </h2>

            <p>
              Notre site contient des liens d'affiliation vers des sites de paris en ligne. Lorsque vous 
              cliquez sur ces liens et vous inscrivez sur un site partenaire, ce site peut collecter 
              vos informations personnelles selon sa propre politique de confidentialité. Nous vous 
              encourageons à lire les politiques de confidentialité de ces sites avant de fournir vos 
              informations.
            </p>

            <p>
              Nous pouvons recevoir des informations agrégées et anonymisées de nos partenaires concernant 
              les clics et les inscriptions générés par nos liens d'affiliation, mais nous ne recevons 
              jamais vos données personnelles identifiables de ces partenaires.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              6. Conservation de vos données
            </h2>

            <p>
              Nous conservons vos données personnelles uniquement aussi longtemps que nécessaire pour 
              les finalités pour lesquelles elles ont été collectées, ou conformément aux obligations 
              légales applicables :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Données de contact : conservées jusqu'à ce que vous demandiez leur suppression ou pendant 3 ans d'inactivité.</li>
              <li>Données de navigation : conservées pendant 13 mois maximum.</li>
              <li>Cookies : selon leur durée de vie (voir notre Politique des cookies).</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              7. Vos droits
            </h2>

            <p>
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Droit d'accès :</strong> Vous pouvez demander une copie de vos données personnelles que nous détenons.</li>
              <li><strong>Droit de rectification :</strong> Vous pouvez demander la correction de données inexactes ou incomplètes.</li>
              <li><strong>Droit à l'effacement :</strong> Vous pouvez demander la suppression de vos données dans certains cas.</li>
              <li><strong>Droit à la limitation du traitement :</strong> Vous pouvez demander la limitation du traitement de vos données.</li>
              <li><strong>Droit à la portabilité :</strong> Vous pouvez demander le transfert de vos données vers un autre service.</li>
              <li><strong>Droit d'opposition :</strong> Vous pouvez vous opposer au traitement de vos données pour certaines finalités.</li>
              <li><strong>Droit de retirer votre consentement :</strong> Si le traitement est basé sur votre consentement, vous pouvez le retirer à tout moment.</li>
            </ul>

            <p>
              Pour exercer ces droits, contactez-nous à : <a href="mailto:info@parisparihub.com" className="text-primary-red hover:underline">info@parisparihub.com</a>
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              8. Sécurité de vos données
            </h2>

            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger 
              vos données personnelles contre l'accès non autorisé, la perte, la destruction ou l'altération :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Chiffrement SSL/TLS pour les transmissions de données</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Surveillance régulière de nos systèmes de sécurité</li>
              <li>Formation de notre personnel sur la protection des données</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              9. Transferts internationaux
            </h2>

            <p>
              Vos données personnelles sont principalement stockées et traitées au sein de l'Union 
              Européenne. Si nous devons transférer vos données vers des pays tiers, nous nous assurons 
              que des garanties appropriées sont en place conformément au RGPD.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              10. Modifications de cette politique
            </h2>

            <p>
              Nous pouvons modifier cette politique de confidentialité de temps à autre. Toute modification 
              sera publiée sur cette page avec une date de mise à jour révisée. Nous vous encourageons 
              à consulter régulièrement cette page pour rester informé de la façon dont nous protégeons 
              vos données.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              11. Contact
            </h2>

            <p>
              Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
              contactez-nous :
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mt-4">
              <p className="font-semibold text-gray-900">ParisPari Hub</p>
              <p className="text-gray-700">Email : <a href="mailto:info@parisparihub.com" className="text-primary-red hover:underline">info@parisparihub.com</a></p>
              <p className="text-gray-700">Téléphone : <a href="tel:+33412407011" className="text-primary-red hover:underline">+33 4 12 40 70 11</a></p>
              <p className="text-gray-700">Adresse : Paris, France</p>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

